/**
 * A date field that speaks calendar days, not instants.
 *
 * Webwin's `DatePicker` holds a `Date` normalized to LOCAL midnight, which is right for a calendar
 * control and wrong as a value to send anywhere: every date this app collects is a calendar day, and
 * the server pins each one to midday UTC so it renders as itself at every offset on earth. So the
 * wrapper reads and writes `yyyy-MM-dd` and nothing else crosses the boundary.
 *
 * This is the same rule the invoice due date and the recurring-expense generator both learned the
 * hard way - a day-1 template whose rows displayed as the 31st, the 30th and the 28th - and keeping
 * it in one wrapper is what stops the third page from learning it again.
 */
import { DatePicker, type DatePickerOptions } from 'webwin';

/** `yyyy-MM-dd` for the LOCAL calendar day a picked date names. */
function localIso(value: Date): string {
  const m = String(value.getMonth() + 1).padStart(2, '0');
  const d = String(value.getDate()).padStart(2, '0');
  return value.getFullYear() + '-' + m + '-' + d;
}

/** A local `Date` at midnight for `yyyy-MM-dd`, which is the shape the picker compares by day. */
function localFromIso(text: string): Date | null {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(text.trim());
  if (!match) return null;
  const when = new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3]));
  return Number.isNaN(when.getTime()) ? null : when;
}

export interface DateBox {
  readonly picker: DatePicker;
  /** `yyyy-MM-dd`, or '' when the field is empty. */
  iso(): string;
  /** Sets from `yyyy-MM-dd`. '' or null clears it. */
  set(iso: string | null | undefined): void;
}

export function dateBox(
  options: Omit<DatePickerOptions, 'value' | 'onChange'> & {
    iso?: string;
    onChange?: () => void;
  } = {}
): DateBox {
  const { iso, onChange, ...rest } = options;
  const picker = new DatePicker({
    height: 30,
    hAlign: 'stretch',
    // The field reads as the value that will be sent, so there is nothing to translate when
    // comparing what is on screen against what a preview says.
    format: localIso,
    ...rest,
    ...(iso ? { value: localFromIso(iso) } : {}),
    ...(onChange ? { onChange: () => onChange() } : {})
  });

  return {
    picker,
    iso: () => (picker.value ? localIso(picker.value) : ''),
    set: next => {
      picker.value = next ? localFromIso(next) : null;
    }
  };
}
